import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';

import { FileUploadsService } from '@app/services/file-uploads.service';
import { PropertiesService } from '@app/services/properties.service';
import { RestapiService } from '@app/services/restapi.service';
import { SurveysService } from '@app/services/surveys.service';
import { UnitsService } from '@app/services/units.service';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  createNewSpaceTemplate,
  deleteSpaceTemplate,
  loadAllCategories,
  loadAllCategoriesFailed,
  loadAllCategoriesSuccess,
  loadAllSpaceTemplates,
  loadAllSpaceTemplatesFailed,
  loadAllSpaceTemplatesSuccess,
  updateSpaceTemplateDetails,
  updateSpaceTemplateDetailsFailed,
} from './space-template.actions';
import {
  RestSharedInspectionSpaceCategory,
  RestSpaceTemplateModel,
} from '../models/rest-space-template-model.interface';

@Injectable()
export class SpaceTemplateEffects {
  loadAllTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllSpaceTemplates),
      exhaustMap(() =>
        this.restApiService.getData<RestSpaceTemplateModel[]>(`inspections/sharedSpaces/list`).pipe(
          map(result => loadAllSpaceTemplatesSuccess({ listItems: result || [] })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.LoadingSpaceTemplatesList);
            return of(loadAllSpaceTemplatesFailed());
          })
        )
      )
    )
  );

  loadAllCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllCategories),
      exhaustMap(() =>
        this.restApiService
          .getData<RestSharedInspectionSpaceCategory[]>(`inspections/sharedSpaces/categories/list`)
          .pipe(
            map(result => loadAllCategoriesSuccess({ categoriesList: result || [] })),
            catchError(err => {
              this.snackbarService.error(SnackbarErrorMessage.LoadingCategories);
              return of(loadAllCategoriesFailed());
            })
          )
      )
    )
  );

  updateSpaceTemplateDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateSpaceTemplateDetails),
      mergeMap(action =>
        this.restApiService
          .post<RestSpaceTemplateModel>(
            `inspections/sharedSpaces/${action.entityDetails.id}/edit`,
            action.entityDetails
          )
          .pipe(
            mergeMap(() => [loadAllSpaceTemplates()]),
            catchError(err => {
              this.snackbarService.error(SnackbarErrorMessage.UpadtingSpaceTemplate);
              return of(updateSpaceTemplateDetailsFailed());
            })
          )
      )
    )
  );

  createNewSpaceTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createNewSpaceTemplate),
      mergeMap(action =>
        this.restApiService.create<number>(`inspections/sharedSpaces/add`, action.entityDetails).pipe(
          tap(id =>
            this.router
              .navigate([AppRoutes.SETTINGS_SPACE_TEMPLATE, id], {
                queryParamsHandling: 'preserve',
              })
              .then()
          ),
          mergeMap(() => [loadAllSpaceTemplates()]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.WhileCreatingNewSpaceTemplate);
            return of(updateSpaceTemplateDetailsFailed());
          })
        )
      )
    )
  );

  deleteSpaceTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteSpaceTemplate),
      mergeMap(action =>
        this.restApiService.post<number>(`inspections/sharedSpaces/${action.id}/delete`).pipe(
          map(() => loadAllSpaceTemplates()),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.RemovingSpaceTemplate);
            return of(updateSpaceTemplateDetailsFailed());
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private restApiService: RestapiService,
    private surveyService: SurveysService,
    private snackbarService: SnackbarService,
    private propertiesService: PropertiesService,
    private store: Store<{}>,
    private router: Router,
    private unitsService: UnitsService,
    private fileUploadsService: FileUploadsService
  ) {}
}
